import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import * as API from '../apiConfig';

function Bills() {


    const navigate = useNavigate();
    const { authData, onLogout } = useOutletContext();
    const authToken = authData?.token;
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
    const [isBillsModalOpen, setIsBillsModalOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [orders, setOrders] = useState([]);
    const [bills, setBills] = useState([]);
    const [selectedBill, setSelectedBill] = useState(null);
    const [selectedClientIdForBill, setSelectedClientIdForBill] = useState('');
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [billClientSearch, setBillClientSearch] = useState('');
    const [orderClientSearch, setOrderClientSearch] = useState('');
    const [editingClientId, setEditingClientId] = useState(null);
    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [isSubmittingClient, setIsSubmittingClient] = useState(false);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
    const [isLoadingBills, setIsLoadingBills] = useState(false);
    const [isSubmittingBill, setIsSubmittingBill] = useState(false);
    const [isLoadingBillDetails, setIsLoadingBillDetails] = useState(false);
    const [clientMessage, setClientMessage] = useState('');
    const [clientError, setClientError] = useState('');
    const [orderMessage, setOrderMessage] = useState('');
    const [orderError, setOrderError] = useState('');
    const [billMessage, setBillMessage] = useState('');
    const [billError, setBillError] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploadedUrl, setUploadedUrl] = useState(null);

    const [isScanning, setIsScanning] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [editedOrder, setEditedOrder] = useState({});
    const [saving, setSaving] = useState(false);


    const getRequestHeaders = useCallback(() => {
        const headers = { 'Content-Type': 'application/json' };
        if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
        }
        return headers;
    }, [authToken]);


    const fetchBills = useCallback(async (clientId) => {
        if (!clientId) {
            setBills([]);
            setSelectedBill(null);
            setBillMessage('');
            setBillError('');
            return;
        }

        setIsLoadingBills(true);
        setBillError('');
        try {
            const response = await fetch(`${API.BILLS_API_URL}/client/${clientId}`, {
                headers: getRequestHeaders(),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Unable to load bills');
            }
            setBills(data.bills || []);
            setBillMessage(
                data.message ||
                `${(data.bills || []).length} bill${(data.bills || []).length === 1 ? '' : 's'
                } loaded`
            );
        } catch (fetchError) {
            setBillError(fetchError.message || 'Unable to load bills');
        } finally {
            setIsLoadingBills(false);
        }
    }, [getRequestHeaders]);

    const fetchOrders = useCallback(async () => {
        setIsLoadingOrders(true);
        setOrderError('');
        try {
            const response = await fetch(API.ORDERS_API_URL, { headers: getRequestHeaders() });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Unable to load orders');
            }
            setOrders(data.orders || []);
            setOrderMessage(data.message || 'Orders retrieved successfully');
        } catch (fetchError) {
            setOrderError(fetchError.message || 'Unable to load orders');
        } finally {
            setIsLoadingOrders(false);
        }
    }, [getRequestHeaders]);

    const resetBillSelection = () => {
        setBillClientSearch('');
        setSelectedClientIdForBill('');
        setSelectedOrderIds([]);
        setSelectedBill(null);
    };

    const fetchBillById = useCallback(
        async (billId) => {
            setIsLoadingBillDetails(true);
            setBillError('');
            try {
                const response = await fetch(`${API.BILLS_API_URL}/${billId}`, {
                    headers: getRequestHeaders(),
                });
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || 'Unable to load bill details');
                }
                setSelectedBill(data.bill || null);
            } catch (fetchError) {
                setBillError(fetchError.message || 'Unable to load bill details');
            } finally {
                setIsLoadingBillDetails(false);
            }
        },
        [getRequestHeaders]
    );

    const handleDeleteBill = async (billId) => {
        const isConfirmed = window.confirm(
            `Are you sure you want to delete bill #${billId}?`
        );

        if (!isConfirmed) {
            return;
        }

        setBillError('');
        setBillMessage('');

        try {
            const response = await fetch(`${API.BILLS_API_URL}/${billId}`, {
                method: 'DELETE',
                headers: getRequestHeaders(),
            });

            let data = {};
            try {
                data = await response.json();
            } catch {
                data = {};
            }

            if (!response.ok) {
                throw new Error(data.message || 'Unable to delete bill');
            }

            setBillMessage(data.message || 'Bill deleted successfully');

            if (selectedBill?.id === billId) {
                setSelectedBill(null);
            }

            await Promise.all([fetchBills(selectedClientIdForBill), fetchOrders()]);
        } catch (deleteError) {
            setBillError(deleteError.message || 'Unable to delete bill');
        }
    };

    const handleExportBillPdf = () => {
        if (!selectedBill) {
            setBillError('Select a bill before exporting');
            return;
        }

        const selectedClient = clients.find(
            (client) => String(client.id) === String(selectedClientIdForBill)
        );
        const clientName = selectedClient?.clientName || 'Unknown Client';
        const qrCodePath = selectedBill.qrCodePath || '';

        const escapeHtml = (value) =>
            String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

        const formatMoney = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;
        const prefix = (clientName || '').slice(0, 3).toUpperCase() || 'BIL';

        const orderRows = (selectedBill.orders || [])
            .map(
                (order, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(`${prefix}-${order.orderId ?? index + 1}`)}</td>
            <td>${escapeHtml(order.description || '')}</td>
            <td class="num">${Number(order.height || 0).toFixed(2)}</td>
            <td class="num">${Number(order.width || 0).toFixed(2)}</td>
            <td class="num">${Number(order.quantity || 0).toFixed(0)}</td>
            <td class="num">${Number(order.totalSquareFeet || 0).toFixed(2)}</td>
            <td class="num">${formatMoney(order.rate)}</td>
            <td class="num">${formatMoney(order.amount)}</td>
          </tr>
        `
            )
            .join('');

        const printWindow = window.open('', '_blank', 'width=900,height=700');

        if (!printWindow) {
            setBillError('Popup blocked. Please allow popups to export the bill.');
            return;
        }

        printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>Bill-${escapeHtml(selectedBill.id)}</title>
          <style>
            @page {
              size: A4;
              margin: 12mm;
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: "Segoe UI", Tahoma, Arial, sans-serif;
              color: #1f2937;
              margin: 0;
              font-size: 10px;
              line-height: 1.35;
              background: #fff;
            }
            .bill {
              border: 1px solid #d1d5db;
              border-radius: 10px;
              overflow: hidden;
            }
            .top-band {
              height: 6px;
              background: #0f4c81;
            }
            .content {
              padding: 14px;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 14px;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 10px;
              margin-bottom: 10px;
            }
            .brand {
              margin: 0 0 4px;
              font-size: 20px;
              font-weight: 700;
              letter-spacing: 0.3px;
              color: #0f172a;
            }
            .subtitle {
              margin: 0;
              color: #64748b;
              font-size: 9px;
            }
            .meta-grid {
              display: grid;
              grid-template-columns: 64px 1fr;
              gap: 2px 8px;
              margin-top: 8px;
              color: #334155;
              font-size: 9px;
            }
            .meta-label {
              color: #64748b;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.2px;
            }
            .qr-box {
              border: 1px solid #d1d5db;
              border-radius: 8px;
              padding: 6px;
              min-width: 102px;
              text-align: center;
            }
            .qr-box img {
              width: 88px;
              height: 88px;
              object-fit: contain;
              display: block;
              margin: 0 auto 4px;
            }
            .qr-label {
              margin: 0;
              color: #64748b;
              font-size: 8px;
            }
            .table-wrap {
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              overflow: hidden;
              margin-top: 8px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              font-size: 9px;
            }
            thead th {
              background: #f8fafc;
              color: #334155;
              text-transform: uppercase;
              font-size: 8px;
              letter-spacing: 0.3px;
              font-weight: 700;
              border-bottom: 1px solid #e5e7eb;
              padding: 7px 6px;
              text-align: left;
            }
            tbody td {
              border-bottom: 1px solid #f1f5f9;
              padding: 6px;
              color: #111827;
            }
            tbody tr:last-child td {
              border-bottom: none;
            }
            .num {
              text-align: left;
            }
            .totals {
              margin-top: 10px;
              display: flex;
              justify-content: flex-end;
            }
            .total-box {
              min-width: 220px;
              border: 1px solid #cbd5e1;
              border-radius: 8px;
              overflow: hidden;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 10px;
              font-size: 10px;
            }
            .total-row.grand {
              background: #eff6ff;
              font-weight: 700;
              color: #0f172a;
            }
            .footer-note {
              margin-top: 10px;
              color: #64748b;
              font-size: 8px;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="bill">
            <div class="top-band"></div>
            <div class="content">
              <div class="header">
                <div>
                  <h1 class="brand">Unique Mirror</h1>
                  <p class="subtitle">Official Billing Document</p>
                  <div class="meta-grid">
                    <span class="meta-label">Bill</span><span>#${escapeHtml(selectedBill.id)}</span>
                    <span class="meta-label">Client</span><span>${escapeHtml(clientName)}</span>
                    <span class="meta-label">Date</span><span>${escapeHtml(selectedBill.formattedDate || selectedBill.date || '')}</span>
                    <span class="meta-label">Orders</span><span>${(selectedBill.orders || []).length}</span>
                  </div>
                </div>
                ${qrCodePath
                ? `
                    <div class="qr-box">
                      <img src="${escapeHtml(qrCodePath)}" alt="Bill QR Code" />
                      <p class="qr-label">Scan for bill reference</p>
                    </div>
                  `
                : ''
            }
              </div>

              <div class="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 5%;">#</th>
                      <th style="width: 14%;">Order ID</th>
                      <th style="width: 23%;">Description</th>
                      <th style="width: 7%;">H</th>
                      <th style="width: 7%;">W</th>
                      <th style="width: 8%;">Qty</th>
                      <th style="width: 10%;">Sq Ft</th>
                      <th style="width: 12%;">Rate</th>
                      <th style="width: 14%;">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${orderRows}
                  </tbody>
                </table>
              </div>

              <div class="totals">
                <div class="total-box">
                  <div class="total-row grand">
                    <span>Total Amount</span>
                    <span>${formatMoney(selectedBill.totalAmount)}</span>
                  </div>
                </div>
              </div>

              <p class="footer-note">Thank you for your business.</p>
            </div>
          </div>
        </body>
      </html>
    `);

        printWindow.document.close();
        printWindow.focus();

        let hasPrinted = false;
        const triggerPrint = () => {
            if (hasPrinted) {
                return;
            }
            hasPrinted = true;
            printWindow.print();
        };

        if (!qrCodePath) {
            triggerPrint();
            return;
        }

        const qrImage = printWindow.document.querySelector('.qr-box img');
        if (!qrImage || qrImage.complete) {
            triggerPrint();
            return;
        }

        qrImage.addEventListener('load', triggerPrint, { once: true });
        qrImage.addEventListener('error', triggerPrint, { once: true });
        setTimeout(triggerPrint, 1500);
    };

    const handleBillClientSearchChange = ({ target: { value } }) => {
        setBillClientSearch(value);

        if (!value.trim()) {
            setSelectedClientIdForBill('');
            setSelectedOrderIds([]);
            setSelectedBill(null);
        }
    };

    const filteredClientsForBillSearch = useMemo(() => {
        const searchValue = billClientSearch.trim().toLowerCase();

        if (!searchValue) {
            return clients.slice(0, 6);
        }

        return clients
            .filter((client) =>
                `${client.clientName || ''} ${client.phoneNumber || ''}`
                    .toLowerCase()
                    .includes(searchValue)
            )
            .slice(0, 6);
    }, [billClientSearch, clients]);

    const handleSelectBillClient = (client) => {
        const selectedClientId = String(client.id);
        setBillClientSearch(client.clientName || '');
        setSelectedClientIdForBill(selectedClientId);
        setSelectedOrderIds([]);
        setSelectedBill(null);
        fetchBills(selectedClientId);
    };
    const filteredOrdersForBilling = useMemo(() => {
        if (!selectedClientIdForBill) {
            return [];
        }

        return orders.filter(
            (order) =>
                String(order.clientId) === String(selectedClientIdForBill) && !order.billed
        );
    }, [orders, selectedClientIdForBill]);

    const selectedBillTotal = useMemo(
        () =>
            filteredOrdersForBilling
                .filter((order) => selectedOrderIds.includes(order.id))
                .reduce((sum, order) => sum + Number(order.amount || 0), 0),
        [filteredOrdersForBilling, selectedOrderIds]
    );



    const handleToggleOrderSelection = (orderId) => {
        setSelectedOrderIds((current) =>
            current.includes(orderId)
                ? current.filter((id) => id !== orderId)
                : [...current, orderId]
        );
    };

    const handleBillSubmit = async () => {
        if (!selectedClientIdForBill || selectedOrderIds.length === 0) {
            setBillError('Select a client and at least one unbilled order');
            return;
        }

        setIsSubmittingBill(true);
        setBillError('');
        setBillMessage('');

        try {
            const response = await fetch(API.BILLS_API_URL, {
                method: 'POST',
                headers: getRequestHeaders(),
                body: JSON.stringify({
                    clientId: Number(selectedClientIdForBill),
                    orderIds: selectedOrderIds,
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Unable to generate bill');
            }
            setBillMessage(data.message || 'Bill generated successfully');
            setSelectedBill(data.bill || null);
            setSelectedOrderIds([]);
            await Promise.all([fetchBills(selectedClientIdForBill), fetchOrders()]);
        } catch (submitError) {
            setBillError(submitError.message || 'Unable to generate bill');
        } finally {
            setIsSubmittingBill(false);
        }
    };

    const fetchClients = useCallback(async () => {
        setIsLoadingClients(true);
        setClientError('');

        try {
            const response = await fetch(API.CLIENTS_API_URL, {
                headers: getRequestHeaders()
            });

            // 🔴 Handle 403 globally here
            if (response.status === 403) {
                onLogout();
                return;
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Unable to load clients');
            }

            setClients(data.clients || []);
            setClientMessage(data.message || 'Clients retrieved successfully');

        } catch (fetchError) {
            setClientError(fetchError.message || 'Unable to load clients');
        } finally {
            setIsLoadingClients(false);
        }
    }, [getRequestHeaders, onLogout]);

    const handleGenerateBills = async () => {
        try {
            const res = await fetch(`${API.BILLS_API_URL}/generate-bill`, {
                method: "GET",
                headers: getRequestHeaders(),
            });

            if (!res.ok) {
                throw new Error("Failed to generate bills");
            }

            const data = await res.json();
            console.log(data)

            if (Array.isArray(data) && data.length > 0) {
                navigate("/generated-bills", { state: { bills: data } });
            } else {
                alert("No orders in delivered stage to generate bill");
            }

        } catch (error) {
            console.error(error);
            alert("Error generating bills");
        }
    };

    useEffect(() => {
        fetchOrders();
        fetchClients();
    }, [fetchClients, fetchOrders]);


    return (
        <div className="modal-body p-4">
            <div className="row g-4">
                <div className="col-12 col-lg-5">
                    <div className="client-form-panel p-4">
                        <h3 className="h5 fw-bold mb-3">Create bill</h3>
                        {billMessage && <div className="alert alert-success">{billMessage}</div>}
                        {billError && <div className="alert alert-danger">{billError}</div>}
                        <div className="mt-3 mb-4">
                            <label className="form-label" htmlFor="bill-client-search">Filter by client</label>
                            <input
                                id="bill-client-search"
                                type="text"
                                className="form-control"
                                placeholder="Search client by name or phone"
                                value={billClientSearch}
                                onChange={handleBillClientSearchChange}
                                autoComplete="off"
                            />
                            <div className="client-search-results mt-2">
                                {filteredClientsForBillSearch.map((client) => (
                                    <button
                                        key={client.id}
                                        type="button"
                                        className={`client-search-item ${String(selectedClientIdForBill) === String(client.id) ? 'active' : ''
                                            }`}
                                        onClick={() => handleSelectBillClient(client)}
                                    >
                                        <span>{client.clientName}</span>
                                        <small>{client.phoneNumber}</small>
                                    </button>
                                ))}
                                {billClientSearch.trim() && filteredClientsForBillSearch.length === 0 && (
                                    <div className="text-secondary small px-2 py-1">
                                        No matching clients found.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="order-summary-box mb-3">
                            <span className="info-chip-label">Selected orders</span>
                            <strong>{selectedOrderIds.length}</strong>
                            <strong>Total amount: {selectedBillTotal.toFixed(2)}</strong>
                        </div>
                        <div className="bill-selection-list mb-4">
                            {!selectedClientIdForBill && (
                                <p className="text-secondary mb-0">Choose a client to see available unbilled orders.</p>
                            )}
                            {selectedClientIdForBill && filteredOrdersForBilling.length === 0 && (
                                <p className="text-secondary mb-0">No unbilled orders available for this client.</p>
                            )}
                            {filteredOrdersForBilling.map((order) => (
                                <label key={order.id} className="bill-order-item">
                                    <input
                                        type="checkbox"
                                        className="form-check-input mt-1"
                                        checked={selectedOrderIds.includes(order.id)}
                                        onChange={() => handleToggleOrderSelection(order.id)}
                                    />
                                    <div>
                                        <strong>{order.description}</strong>
                                        <div className="small text-secondary">
                                            Qty {order.quantity} | Amount {Number(order.amount || 0).toFixed(2)}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-success" onClick={handleBillSubmit} disabled={isSubmittingBill}>
                                {isSubmittingBill ? 'Generating...' : 'Generate Bill'}
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={resetBillSelection} disabled={isSubmittingBill}>
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-7">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <div>
                            <h3 className="h5 fw-bold mb-1">Bill history</h3>
                            <p className="text-secondary mb-0">
                                {!selectedClientIdForBill
                                    ? 'Select a client to load bills'
                                    : isLoadingBills
                                        ? 'Loading bills...'
                                        : `${bills.length} bill${bills.length === 1 ? '' : 's'} found`}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-success"
                            onClick={() => fetchBills(selectedClientIdForBill)}
                            disabled={isLoadingBills || !selectedClientIdForBill}
                        >
                            Refresh
                        </button>
                    </div>
                    <div className="table-responsive client-table-wrap mb-3">
                        <table className="table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Bill ID</th>
                                    <th>Date</th>
                                    <th>Orders</th>
                                    <th>Total</th>
                                    <th className="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!selectedClientIdForBill && (
                                    <tr>
                                        <td colSpan="5" className="text-center text-secondary py-4">Choose a client to view bill history.</td>
                                    </tr>
                                )}
                                {selectedClientIdForBill && !isLoadingBills && bills.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center text-secondary py-4">No bills found yet.</td>
                                    </tr>
                                )}
                                {bills.map((bill) => (
                                    <tr key={bill.id}>
                                        <td>#{bill.id}</td>
                                        <td>{bill.formattedDate || bill.date}</td>
                                        <td>{bill.orders?.length || 0}</td>
                                        <td>{Number(bill.totalAmount || 0).toFixed(2)}</td>
                                        <td className="text-end">
                                            <div className="d-inline-flex gap-2">
                                                <button type="button" className="btn btn-sm btn-outline-success" onClick={() => fetchBillById(bill.id)} disabled={isLoadingBillDetails}>
                                                    View
                                                </button>
                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteBill(bill.id)} disabled={isLoadingBills}>
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="bill-details-panel p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="h6 fw-bold mb-0">Bill details</h4>
                            <div className="d-flex align-items-center gap-2">
                                {selectedBill && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        onClick={handleExportBillPdf}
                                    >
                                        Export PDF
                                    </button>
                                )}
                                {isLoadingBillDetails && <span className="small text-secondary">Loading...</span>}
                            </div>
                        </div>
                        {!selectedBill && !isLoadingBillDetails && (
                            <p className="text-secondary mb-0">Select a bill from the list to view its orders.</p>
                        )}
                        {selectedBill && (
                            <>
                                <div className="d-flex flex-wrap gap-3 mb-3">
                                    <div className="info-chip">
                                        <span className="info-chip-label">Bill ID</span>
                                        <strong>#{selectedBill.id}</strong>
                                    </div>
                                    <div className="info-chip">
                                        <span className="info-chip-label">Date</span>
                                        <strong>{selectedBill.formattedDate || selectedBill.date}</strong>
                                    </div>
                                    <div className="info-chip">
                                        <span className="info-chip-label">Total</span>
                                        <strong>{Number(selectedBill.totalAmount || 0).toFixed(2)}</strong>
                                    </div>
                                </div>
                                <div className="table-responsive client-table-wrap">
                                    <table className="table align-middle mb-0">
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th>Qty</th>
                                                <th>Rate</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedBill.orders?.map((order) => (
                                                <tr key={order.id}>
                                                    <td>{order.description}</td>
                                                    <td>{order.quantity}</td>
                                                    <td>{Number(order.rate || 0).toFixed(2)}</td>
                                                    <td>{Number(order.amount || 0).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Bills;
